/* pure adapters: the goserver penalties payloads -> view models. */
import { EM_DASH, MONTH_SHORT, fmt, fmt_money, month_label, num } from '../format';
import { rule_ref_of } from '../rules_engine/logic';
import {
  BULK_ACTION,
  DAY_ACTION,
  GROUP_OUTCOME,
  ITEM_OUTCOME,
  MONTH_PASS_ACTION,
  PASS_STATE,
  PLACED_SOURCE,
  REASON_LABELS,
  ROW_ACTION,
  SOURCE,
  WAIVER_STATUS,
  WARNING,
  is_pass_eligible,
  type_label
} from './params';

// Whole rupees unless the stored amount carries paise (never hidden).
export const rupees = (amount) => fmt_money(num(amount));

// A deduction reads as one: "-₹400"; zero stays "₹0".
export const deduction_text = (amount) => {
  const n = num(amount);
  return n > 0 ? `-${rupees(n)}` : rupees(0);
};

// "2026-06-14" -> "14 Jun" (plain text: a browser time zone never shifts the IST day).
export const day_text = (day) => {
  const m = String(day || '').match(/^\d{4}-(\d{2})-(\d{2})/);
  if (!m) return day || '';
  return `${Number(m[2])} ${MONTH_SHORT[Number(m[1]) - 1] || m[1]}`;
};

export const WAIVER_LABELS = {
  [WAIVER_STATUS.NOT_WAIVED]: 'Not waived',
  [WAIVER_STATUS.WAIVED]: 'Waived',
  [WAIVER_STATUS.FREE_PASS_APPLIED]: 'Free Pass applied'
};

export const SOURCE_LABELS = { [SOURCE.SYSTEM]: 'System', [SOURCE.MANUAL]: 'Manual' };

// a person reads by name; the uuid stays as the tooltip, and stands in when no name came.
export const who_text = (name, uuid) => name || uuid || EM_DASH;

const has = (row, action) =>
  !!row &&
  !!row.editable &&
  Array.isArray(row.editable.actions) &&
  row.editable.actions.indexOf(action) !== -1;

// What the row's state says, in one line (the pill beside the waiver status).
const state_note = (row) => {
  if (row.is_orphaned) return 'Orphaned - not charged';
  if (row.is_overridden && row.source === SOURCE.SYSTEM) return 'Changed by Ops';
  return '';
};

const WAIVER_TONES = {
  [WAIVER_STATUS.NOT_WAIVED]: 'gray',
  [WAIVER_STATUS.WAIVED]: 'blue',
  [WAIVER_STATUS.FREE_PASS_APPLIED]: 'green'
};

// The engine's own value, kept from Ops' first change, so the dialog shows what Revert restores.
const override_text = (o) =>
  o
    ? `System value: ${type_label(o.engine_type)} ${num(o.engine_pct)}% ${deduction_text(
        o.engine_original
      )} · first changed by ${who_text(o.by_name, o.by)} ${o.at || ''}`.trim()
    : '';

// Late join (Ops only): the Join-button click, the actual join (what the charge is measured on) and the
// student's first join (not on a group class, measured from its start). Time alone on the session's IST day.
export const JOIN_TIME_LABELS = [
  ['clicked_join_ist', 'Clicked Join'],
  ['joined_class_ist', 'Joined the class'],
  ['student_joined_ist', 'Student joined']
];

export const ASSIGNED_LABEL = 'Assigned to this teacher at';

export const join_time_lines = (join_times, session_start_ist, category) => {
  if (!join_times || typeof join_times !== 'object') return [];
  const day = String(session_start_ist || '').slice(0, 10);
  const labels =
    category === 'GROUP_CLASS'
      ? JOIN_TIME_LABELS.filter(([key]) => key !== 'student_joined_ist')
      : JOIN_TIME_LABELS;
  const text = (at) => (day && at.slice(0, 10) === day ? at.slice(11) : at);
  const lines = labels.map(([key, label]) => {
    const at = join_times[key] ? String(join_times[key]) : '';
    return { label, value: at ? text(at) : 'not recorded' };
  });
  // Only a teacher the class reached by replacement or a Care swap has this line.
  return join_times.assigned_ist
    ? [{ label: ASSIGNED_LABEL, value: text(String(join_times.assigned_ist)) }, ...lines]
    : lines;
};

// Every affordance comes from editable.actions (hridaya guardrail); why_not explains the missing ones.
export const row_view = (row) => {
  const money = row.money || {};
  const corrected = row.corrected_amount !== null && row.corrected_amount !== undefined;
  const basis = corrected ? num(row.corrected_amount) : num(row.original_amount);
  return {
    key: row.uuid,
    uuid: row.uuid,
    short_id: row.short_id || String(row.uuid || '').slice(-8),
    version: row.version,
    teacher_uuid: row.teacher_uuid,
    teacher_name: row.teacher_name || EM_DASH,
    teacher_slug: row.teacher_slug || '',
    pay_month: row.pay_month,
    session_uuid: row.session_uuid || '',
    session_text: row.session_uuid ? row.session_start_ist || row.session_uuid : 'Month-level',
    student_name: row.student_name || EM_DASH,
    penalty_type: row.penalty_type,
    type_text: row.penalty_type_label || type_label(row.penalty_type),
    source: row.source,
    source_text: SOURCE_LABELS[row.source] || row.source,
    is_manual: row.source === SOURCE.MANUAL,
    pct_text: row.session_uuid ? `${num(row.penalty_pct)}%` : EM_DASH,
    penalty_pct: num(row.penalty_pct),
    base_rate: num(row.base_rate_used),
    base_rate_text: num(row.base_rate_used) > 0 ? rupees(row.base_rate_used) : EM_DASH,
    original_text: deduction_text(row.original_amount),
    corrected_text: corrected ? deduction_text(row.corrected_amount) : '',
    basis,
    charged: num(money.charged),
    charged_text: deduction_text(money.charged),
    waived_text: num(money.waived) ? rupees(money.waived) : '',
    pass_text: num(money.pass) ? rupees(money.pass) : '',
    uncharged_orphan_text: num(money.uncharged_orphan) ? rupees(money.uncharged_orphan) : '',
    waiver_status: row.waiver_status,
    waiver_text: WAIVER_LABELS[row.waiver_status] || row.waiver_status || EM_DASH,
    waiver_tone: WAIVER_TONES[row.waiver_status] || 'gray',
    state_text: state_note(row),
    reason_text: row.reason_code ? REASON_LABELS[row.reason_code] || row.reason_code : '',
    approved_by: row.approved_by || '',
    note: row.note || '',
    rule_ref: rule_ref_of(row),
    day_ist: row.day_ist || '',
    day_text: row.day_ist ? day_text(row.day_ist) : '',
    updated_text: row.updated_by
      ? `${who_text(row.updated_by_name, row.updated_by)} · ${row.updated_at_ist || ''}`
      : '',
    updated_by_title: row.updated_by || '',
    needs_review: row.month_needs_review === true,
    override_text: override_text(row.override),
    explain: row.explain || null,
    join_lines: join_time_lines(row.join_times, row.session_start_ist, row.category),
    actions: Object.values(ROW_ACTION).filter((a) => has(row, a)),
    why_not: (row.editable && row.editable.why_not) || '',
    can_waive: has(row, ROW_ACTION.WAIVE),
    can_apply_pass: has(row, ROW_ACTION.APPLY_PASS),
    // the server's flag; an older server without it falls back to the type list
    free_pass_eligible:
      typeof row.free_pass_eligible === 'boolean'
        ? row.free_pass_eligible
        : is_pass_eligible(row.penalty_type),
    row
  };
};

export const list_view = (data) => {
  const d = data || {};
  const raw = Array.isArray(d.rows) ? d.rows : [];
  return {
    rows: raw.map(row_view),
    // Unsorted (the default id order) pages by next_after_id; a column sort pages by next_offset
    // instead (penalties_list_sort.go) — exactly one of the two is non-null at a time.
    next_after_id:
      d.next_after_id === null || d.next_after_id === undefined ? null : d.next_after_id,
    next_offset: d.next_offset === null || d.next_offset === undefined ? null : d.next_offset
  };
};

// "Load more" appends the next page (keyset or, sorted, offset); a row on both pages is kept once.
export const append_page = (view, next) => {
  const seen = new Set(view.rows.map((r) => r.uuid));
  return {
    ...next,
    rows: view.rows.concat(next.rows.filter((r) => !seen.has(r.uuid)))
  };
};

// Whether the list has a next page, sorted or not.
export const has_more = (view) => view.next_after_id !== null || view.next_offset !== null;

// with this figure the summary adds back to the originals; shown only when non-zero.
const ORPHAN_LABEL = 'Uncharged (orphaned)';

const money_chips = (c) =>
  [
    num(c.waived) ? `Waived ${rupees(c.waived)}` : '',
    num(c.pass) ? `Free Pass ${rupees(c.pass)}` : '',
    num(c.correction) ? `Corrections ${rupees(c.correction)}` : '',
    num(c.uncharged_orphan) ? `${ORPHAN_LABEL} ${rupees(c.uncharged_orphan)}` : ''
  ].filter(Boolean);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// the live policy map (penalty_type -> pct | null), shared by the tab's summary and the session
// page's Add dialog so both pre-fill from the same shape.
export const default_pcts_map = (list) =>
  Object.fromEntries((Array.isArray(list) ? list : []).map((p) => [p.penalty_type, p.default_pct]));

// default: free-pass cover is its own figure, never inside Waivers.
export const summary_view = (data) => {
  const d = data || {};
  const t = d.totals || {};
  return {
    totals: [
      {
        key: 'charged',
        label: 'Penalties charged',
        value: deduction_text(t.charged),
        sub: `${plural(num(t.records), 'record', 'records')} · ${plural(num(t.teachers), 'teacher', 'teachers')}`,
        tone: 'neg'
      },
      { key: 'waived', label: 'Waivers', value: rupees(t.waived), sub: 'Waived by Ops' },
      { key: 'pass', label: 'Free Pass', value: rupees(t.pass), sub: 'Covered by Free Passes' },
      {
        key: 'correction',
        label: 'Corrections',
        value: rupees(t.correction),
        sub: 'Original minus corrected'
      },
      ...(num(t.uncharged_orphan)
        ? [
            {
              key: 'uncharged_orphan',
              label: ORPHAN_LABEL,
              value: rupees(t.uncharged_orphan),
              sub: 'Edited penalties whose reason is gone'
            }
          ]
        : [])
    ],
    cards: (Array.isArray(d.cards) ? d.cards : []).map((c) => ({
      key: c.penalty_type,
      penalty_type: c.penalty_type,
      label: c.label || type_label(c.penalty_type),
      value: deduction_text(c.charged),
      sub: `${plural(num(c.count), 'penalty', 'penalties')} · ${plural(num(c.teachers), 'teacher', 'teachers')}`,
      chips: money_chips(c)
    })),
    default_pcts: default_pcts_map(d.default_pcts)
  };
};

// ---- mutations ----

const net_of = (totals) => (totals && totals.net !== undefined ? num(totals.net) : null);

// The change is saved even when the recompute fails (two transactions): never imply the money is final.
export const recompute_text = (recompute) => {
  const r = recompute || {};
  if (r.ok === false) {
    return 'Saved; recalculation failed. The month shows "needs review" until it recomputes.';
  }
  if (r.skipped) {
    return `Saved; the month was not recalculated${r.skip_reason ? ` (${r.skip_reason})` : ''}.`;
  }
  const before = net_of(r.totals_before);
  const after = net_of(r.totals_after);
  return before !== null && after !== null && before !== after
    ? `Saved. Net ${fmt(before, 2)} → ${fmt(after, 2)}.`
    : 'Saved.';
};

// Every WARNING code needs a fallback, in case the server ever omits its message; params.test.js pins
// WARNING to the published warning_codes, and adapters.test.js pins this map to WARNING (B7-style).
export const WARNING_FALLBACKS = {
  [WARNING.RECOMPUTE_FAILED]: 'The month could not be recalculated.',
  [WARNING.TEACHER_HAS_NO_BASE_RATE]: 'This teacher has no base rate, so the month cannot compute.',
  [WARNING.MONTH_WILL_GO_NEGATIVE]: 'This month now nets below zero; the deficit carries forward.',
  [WARNING.PIN_DISPLACES_AUTO]: 'This Free Pass took the automatic pass from another day.',
  [WARNING.PASS_COVERS_NEW_PENALTY]:
    'This penalty is on a day that holds a Free Pass; it is covered.'
};

export const warnings_view = (warnings) =>
  (Array.isArray(warnings) ? warnings : [])
    .filter((w) => w && w.code)
    .map((w) => ({ code: w.code, message: w.message || WARNING_FALLBACKS[w.code] || w.code }));

export const NOTHING_CHANGED_TEXT = 'Nothing changed. The penalty already had these values.';
export const REPLAYED_TEXT = 'Already saved. This penalty was submitted twice and added once.';

// Blocking consequences the recompute opened, so Ops see that the change adds a close blocker.
const issues_text = (after) => {
  const a = after || {};
  const opened = Array.isArray(a.opened) ? a.opened : [];
  if (!opened.length) return '';
  const blockers = num(a.errors_open) + num(a.must_review_open);
  return `${plural(opened.length, 'new issue', 'new issues')} for review in Needs attention${
    blockers ? ` (${plural(blockers, 'item blocks', 'items block')} the month close)` : ''
  }.`;
};

// tone: success | warning (not recalculated) | info (no-op).
export const mutation_result = (data) => {
  const d = data || {};
  const recompute_ok = !(d.recompute && d.recompute.ok === false);
  let message = recompute_text(d.recompute);
  let tone = recompute_ok ? 'success' : 'warning';
  if (d.replayed === true) {
    message = REPLAYED_TEXT;
  } else if (d.changed === false) {
    message = NOTHING_CHANGED_TEXT;
    tone = 'info';
  }
  // A failed recompute's server sentence names the month, so it replaces ours and is not repeated.
  const all = warnings_view(d.warnings);
  const failed = all.find((w) => w.code === WARNING.RECOMPUTE_FAILED);
  if (!recompute_ok && failed && d.replayed !== true) message = failed.message;
  const warnings = all.filter((w) => recompute_ok || w.code !== WARNING.RECOMPUTE_FAILED);
  return {
    penalty: d.penalty || null,
    free_pass: d.free_pass || null,
    removed: d.removed === true,
    changed: d.changed !== false,
    replayed: d.replayed === true,
    recompute_ok,
    tone,
    message,
    issues_text: issues_text(d.issues_after),
    warnings
  };
};

// An add moved to another month saves a row the shown list cannot hold; say where it went.
export const other_month_text = (penalty, shown_month) => {
  const saved = String((penalty && penalty.pay_month) || '').slice(0, 7);
  const shown = String(shown_month || '').slice(0, 7);
  return saved && shown && saved !== shown
    ? `Added to ${month_label(saved)}. This list shows ${month_label(shown)}.`
    : '';
};

// ---- the free-pass state (display only; every write re-validates) ----

export const PASS_STATE_TEXT = {
  [PASS_STATE.PLACED]: 'Free Passes are placed for this month.',
  [PASS_STATE.HELD_PREV_MONTH_OPEN]:
    'On hold: the previous month is still open. Passes are placed once it closes.',
  [PASS_STATE.HELD_LATER_MONTH_CLOSED]:
    'On hold: a later month is already closed. Reopen it for passes to move here.',
  [PASS_STATE.NO_BALANCE]: 'No Free Passes available this month.',
  [PASS_STATE.NO_ELIGIBLE_DAY]: 'No day this month has a penalty a Free Pass can cover.'
};

// The day's pass as a clause: "automatic" or "placed by Ops"; "" when no pass sits there.
const placed_clause = (placed) => {
  if (!placed) return '';
  return placed.source === PLACED_SOURCE.PIN ? 'placed by Ops' : 'automatic';
};

// the month's own Free Pass Day. Only a removal has something to say; the offered action comes from
// the server.
export const month_pass_view = (mp) => {
  const m = mp || {};
  const actions = Array.isArray(m.actions) ? m.actions : [];
  const who = m.by_name || m.by || '';
  const removed = m.removed === true;
  return {
    removed,
    text: removed
      ? `This month's Free Pass Day was removed${who ? ` by ${who}` : ''}${m.at_ist ? ` on ${m.at_ist}` : ''}.`
      : '',
    note: typeof m.note === 'string' ? m.note : '',
    can_remove: actions.indexOf(MONTH_PASS_ACTION.REMOVE) !== -1,
    can_restore: actions.indexOf(MONTH_PASS_ACTION.RESTORE) !== -1
  };
};

export const free_pass_view = (state) => {
  const s = state || {};
  const placed = (Array.isArray(s.placed) ? s.placed : []).map((p) => ({
    day_ist: p.day_ist,
    day_text: day_text(p.day_ist),
    value_text: rupees(p.value),
    source: p.source,
    source_text: p.source === PLACED_SOURCE.PIN ? 'Placed by Ops' : 'Automatic'
  }));
  // actions/move_target come from the server; the box renders only what they say, never a client
  // guess derived from held/value.
  const days = (Array.isArray(s.days) ? s.days : []).map((d) => ({
    day_ist: d.day_ist,
    day_text: day_text(d.day_ist),
    value: num(d.value),
    value_text: rupees(d.value),
    held: !!d.held,
    actions: Array.isArray(d.actions) ? d.actions : [],
    move_target: !!d.move_target,
    placed_text: placed_clause(placed.find((p) => p.day_ist === d.day_ist))
  }));
  const actions = Array.isArray(s.actions) ? s.actions : [];
  const pins = Array.isArray(s.pins) ? s.pins : [];
  const skips = Array.isArray(s.skips) ? s.skips : [];
  return {
    month_text: month_label(s.pay_month),
    auto: s.auto !== false,
    balance_text: `${plural(num(s.opening_balance), 'Free Pass', 'Free Passes')} available`,
    opening_balance: num(s.opening_balance),
    placed_count: placed.length,
    state_text: PASS_STATE_TEXT[s.state] || '',
    placed,
    days,
    // A move target is a day the server names, not one the client infers from value/held.
    move_targets: days.filter((d) => d.move_target),
    unplaced_text: (Array.isArray(s.unplaced_pins) ? s.unplaced_pins : []).map(day_text).join(', '),
    is_custom: s.auto === false || pins.length > 0 || skips.length > 0,
    needs_review: s.needs_review === true,
    actions,
    month_pass: month_pass_view(s.month_pass),
    // Free Pass does not apply to this teacher (server sentence; absent on an older backend).
    excluded_text:
      s.excluded === true ? s.excluded_text || 'Free Pass does not apply to this teacher.' : '',
    raw: s
  };
};

// The row's day in its teacher-month state: its value and whether a pass sits there.
export const pass_day = (view, day) => view.days.find((d) => d.day_ist === day) || null;

// The Waive dialog's "Use a Free Pass Day instead": whether it can, and the line that says why
// (or how many are left). The server's row and day actions decide; the client never guesses.
export const waive_pass_option = (row, pass) => {
  const r = row || {};
  if (!r.free_pass_eligible) {
    return { enabled: false, text: 'A Free Pass Day does not cover this penalty type.' };
  }
  if (!pass || pass.loading) return { enabled: false, text: 'Loading Free Pass Days…' };
  if (pass.error_text) return { enabled: false, text: pass.error_text };
  const v = pass.view;
  if (v.excluded_text) return { enabled: false, text: v.excluded_text };
  const opening = num(v.opening_balance);
  if (opening <= 0)
    return { enabled: false, text: 'This teacher has no Free Pass Days this month.' };
  const day = pass_day(v, r.day_ist);
  if (day && day.held) return { enabled: false, text: 'A Free Pass Day already covers this day.' };
  if (!r.can_apply_pass || !day || day.actions.indexOf(DAY_ACTION.USE_PASS) === -1) {
    return { enabled: false, text: 'A Free Pass Day cannot be used on this penalty’s day.' };
  }
  const left = Math.max(opening - num(v.placed_count), 0);
  return {
    enabled: true,
    text: left
      ? `${left} of ${plural(opening, 'Free Pass Day', 'Free Pass Days')} unused this month. It covers every eligible penalty on ${day.day_text}.`
      : `All ${plural(opening, 'Free Pass Day', 'Free Pass Days')} are in use: using one here moves it from another day.`
  };
};

// ---- bulk ----

// Rows the chosen action can take; the rest are named, never silently dropped.
export const bulk_selection = (rows, action) => {
  const can = (r) => (action === BULK_ACTION.WAIVE ? r.can_waive : r.can_apply_pass);
  return { eligible: rows.filter(can), skipped: rows.filter((r) => !can(r)) };
};

export const selection_total_text = (rows) =>
  deduction_text(rows.reduce((sum, r) => sum + num(r.charged), 0));

// groups from every chunk sent, in order; teacher names come from the selected rows.
export const bulk_result_view = (groups, rows_by_uuid) => {
  const name_of = (g) => {
    const hit = (g.items || []).map((i) => rows_by_uuid[i.penalty_uuid]).find(Boolean);
    return (hit && hit.teacher_name) || g.teacher_uuid;
  };
  const lines = groups.map((g) => {
    const items = Array.isArray(g.items) ? g.items : [];
    const applied = g.outcome === GROUP_OUTCOME.APPLIED;
    const count = items.filter((i) => i.outcome === ITEM_OUTCOME.APPLIED).length;
    const unchanged = items.filter((i) => i.outcome === ITEM_OUTCOME.UNCHANGED).length;
    const recompute_failed = !!g.recompute && g.recompute.ok === false;
    let text = applied
      ? `${plural(count, 'penalty', 'penalties')} saved${unchanged ? `, ${unchanged} already so` : ''}`
      : g.message || `Refused (${g.code || 'no reason given'}); nothing for this teacher was saved`;
    if (applied && recompute_failed) text += '; recalculation failed (needs review)';
    return {
      key: `${g.teacher_uuid}|${g.pay_month}`,
      teacher_name: name_of(g),
      month_text: month_label(g.pay_month),
      applied,
      text
    };
  });
  return {
    lines,
    applied_groups: lines.filter((l) => l.applied).length,
    refused_groups: lines.filter((l) => !l.applied).length
  };
};

// ---- the session review pill ----

export const PILL = { HIDDEN: 'hidden', TO_REVIEW: 'to_review', REVIEWED: 'reviewed' };

// Hidden with no issue; "N to review" while any is OPEN; "Reviewed" once every one is decided.
// names: teacher_uuid -> name. With more than one teacher on the session, each line names its teacher.
export const pill_view = (issues, names = {}) => {
  const list = Array.isArray(issues) ? issues : [];
  const open = list.filter((i) => i.status === 'OPEN');
  const many = new Set(list.map((i) => i.teacher_uuid)).size > 1;
  let state = PILL.HIDDEN;
  if (list.length) state = open.length ? PILL.TO_REVIEW : PILL.REVIEWED;
  return {
    state,
    text: state === PILL.TO_REVIEW ? `${open.length} to review` : 'Reviewed',
    issues: list.map((i) => ({
      id: i.issue_id,
      label: [many ? who_text(names[i.teacher_uuid], i.teacher_uuid) : '', i.label || i.issue_code]
        .filter(Boolean)
        .join(' · '),
      status: i.status,
      status_text: i.status ? i.status.charAt(0) + i.status.slice(1).toLowerCase() : '',
      can_acknowledge: i.acknowledgeable === true && i.status === 'OPEN',
      can_resolve: i.status === 'OPEN' || i.status === 'ACKNOWLEDGED'
    }))
  };
};
